import api from "@/api";
import { IS_IBG_MANAGER } from "@/consts";
import {
  Container_Type,
  type Document,
  type InputMaybe,
  type Maybe,
  type Scalars,
} from "@/graphql/generated-types";
import type { IFileState } from "@/interfaces";
import { useAuthStore } from "@/stores/auth.module";

export type PDFFeatureState = {
  documentToUpload: IFileState[];
};

export type DatabaseModel = {
  documents?: Maybe<Array<Document>>;
};

export type SaveFeatureProps<State> = {
  store: {
    item: State;
  };
  input: {
    documents?: InputMaybe<Array<Scalars["ID"]["input"]>>;
  };
};

export const PDF_FEATURE = {
  getDefault() {
    return {
      documentToUpload: [],
    };
  },

  setState(data: DatabaseModel, state: PDFFeatureState) {
    if (!data.documents || data.documents.length === 0) {
      return;
    }

    const [document] = data.documents;
    if (document?.url.includes(".pdf")) {
      state.documentToUpload = [
        {
          id: document.id,
          name: document.title,
          originalName: document.title,
          url: document.url,
          base64: "",
          type: "pdf",
        },
      ];
    }
  },

  hash(state: PDFFeatureState) {
    return {
      documentToUpload: state.documentToUpload,
    };
  },

  toValidation(state: PDFFeatureState) {
    return {
      documentToUpload: IS_IBG_MANAGER
        ? state.documentToUpload.filter((item) => item.action !== "delete")
        : [],
    };
  },

  async save({ store, input }: SaveFeatureProps<PDFFeatureState>) {
    const authStore = useAuthStore();

    // No need to do anything
    if (store.item.documentToUpload.length === 0) {
      return;
    }

    const pdfToUpload = store.item.documentToUpload.find(
      (item) => item.action === "upload",
    );
    const pdfToUpdate = store.item.documentToUpload.find(
      (item) => !item.action,
    );

    if (pdfToUpload) {
      // Uploading a new PDF file
      const documentId = await api.resource.save({
        container: Container_Type.Pdf,
        body: pdfToUpload.base64,
        institution: authStore.institutionId,
        title: pdfToUpload.name,
      });
      input.documents = [documentId!];
      pdfToUpload.id = documentId!;
      // No need any action
      pdfToUpload.action = undefined;
      // We need to update the original name in case of the name is changed
      pdfToUpload.originalName = pdfToUpload.name;
    } else if (pdfToUpdate) {
      // We should update the name
      if (pdfToUpdate.name !== pdfToUpdate.originalName) {
        // Updating a PDF file. No need to wait to update request here.
        api.resource.save({
          container: Container_Type.Pdf,
          id: pdfToUpdate.id,
          title: pdfToUpdate.name,
        });

        // Let's reset the original name
        pdfToUpdate.originalName = pdfToUpdate.name;
      }

      input.documents = [pdfToUpdate.id!];
    }
  },
};
